import type { CityDto, GeoCoords } from '@/core/data/location/location.interface';
import { haversineDistance } from '@/core/helpers/location.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useCityStore } from '@/stores/city.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { storeToRefs } from 'pinia';
import { computed, type Ref } from 'vue';

export enum AccessLevel {
  NEARBY = 'NEARBY',
  CITY_LEVEL = 'CITY_LEVEL',
  REMOTE = 'REMOTE',
}

const NEARBY_THRESHOLD_METERS = 50;
const GPS_CITY_LEVEL_THRESHOLD_METERS = 20_000;

function isWithinCityViewport(coords: GeoCoords, city: CityDto | null): boolean {
  if (!city?.viewportHigh || !city?.viewportLow) return false;

  return (
    coords.lat >= city.viewportLow.lat &&
    coords.lat <= city.viewportHigh.lat &&
    coords.lng >= city.viewportLow.lng &&
    coords.lng <= city.viewportHigh.lng
  );
}

export function useAccessLevel(spotCoordinates: Ref<GeoCoords | undefined | null>) {
  const geolocationStore = useGeolocationStore();
  const cityStore = useCityStore();
  const authStore = useAuthStore();
  const { city } = storeToRefs(cityStore);

  const { isLocationPermissionGranted } = storeToRefs(geolocationStore);
  const userCoords = computed(() => geolocationStore.userLocation?.location ?? null);
  const hasGps = computed(() => isLocationPermissionGranted.value && !!userCoords.value);
  const admin = computed(() => authStore.isAdmin());

  const distanceToSpot = computed((): number | null => {
    if (!spotCoordinates.value || !userCoords.value || !isLocationPermissionGranted.value) return null;
    return haversineDistance(spotCoordinates.value, userCoords.value);
  });

  const level = computed((): AccessLevel => {
    if (admin.value) return AccessLevel.NEARBY;

    const spot = spotCoordinates.value;
    if (!spot) return AccessLevel.REMOTE;

    if (distanceToSpot.value !== null && distanceToSpot.value <= NEARBY_THRESHOLD_METERS) {
      return AccessLevel.NEARBY;
    }

    if (isWithinCityViewport(spot, city.value)) {
      return AccessLevel.CITY_LEVEL;
    }

    if (distanceToSpot.value !== null && distanceToSpot.value <= GPS_CITY_LEVEL_THRESHOLD_METERS) {
      return AccessLevel.CITY_LEVEL;
    }

    return AccessLevel.REMOTE;
  });

  const isNearby = computed(() => level.value === AccessLevel.NEARBY);
  const isCityLevel = computed(() => level.value === AccessLevel.CITY_LEVEL || level.value === AccessLevel.NEARBY);
  const isRemote = computed(() => level.value === AccessLevel.REMOTE);

  const canPlayBoards = computed(() => isNearby.value);
  const canCheckIn = computed(() => isNearby.value);
  const canSeeActivity = computed(() => isCityLevel.value);

  return {
    level,
    distanceToSpot,
    hasGps,
    isNearby,
    isCityLevel,
    isRemote,
    canPlayBoards,
    canCheckIn,
    canSeeActivity,
  };
}
